import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import LoadingCircle from '@cpg-its/ads.common.loading-circle';
import { DividerSection } from '../../../../global/components/layout/divider-section';
import { MainContent } from '../../../../global/components/layout/main-content-container';
import { PageContainer } from '../../../../global/components/layout/page-container';
import { PageHeader } from '../../../../global/components/layout/page-header';
import { WysiwygWithReactLink } from '../../../../global/components/wysiwyg-with-react-link';
import { getTcsmEpiProps, setMapPortalSettings } from '../../../../global/ducks/epi-api';
import { useBasePath } from '../../../../global/hooks/useBasePath';
import { useEpiPageContent } from '../../../../global/hooks/useEpiPageContent';
import { RedesignNewsUpdatesPage, TcsmNewsUpdatesPage } from '../../../../global/models/tcsm-page-models';
import { TcsmEpiApiState, TcsmStates } from '../../../../root/tcsm-model';
import { useDeserializedToken } from '../../components/use-deserialized-token';
import { ArcRoutes } from '../../constants/routes';
import { useGroupsReset } from '../../hooks/useGroupsReset';
import { usePersonManagementReset, useSearchListsReset } from '../../hooks/usePersonManagementReset';
import { MapPortalProps } from '../../models/arc-epi-props';
import { TcsmSiteSettingsProps } from '../../../../global/models/epi-model';
import { DashboardNotifications } from './components/dashboard-notifications';
import { PortalGuidesAndResources } from './components/portal-guides-resources';
import { PortalQuickActions } from './components/portal-quick-actions';
import { PortalSidebar } from './components/portal-sidebar';
import { DashboardUser, TcsmDashboardUsers } from '../../../../root/tcsm-dashboard-users';
import { PortalLandingBanner } from './components/portal-landing-banner';
import { getNotifications } from '../../../../global/ducks/notification-prc-api';
import { apiErrorClear } from '../../../../global/ducks/base-api';
import { setNotifications } from '../../../notification/notification-slice';
import { NotificationStatus } from '../../../notification/notification-models';
import { useAdminGHLPendingEnrollments } from '../new-hire-enrollment/hooks/useAdminGHLPendingEnrollments';
import { NewHireAdminPendingGhlEnrollments } from '../new-hire/components/new-hire-admin-pending-ghl-enrollments';
import { AdminGhlPendingEnrollment } from '../new-hire-enrollment/member/models/member-coverage-model';
import { removePendingEnrollmentLocalStorage } from '../new-hire-enrollment/member/benefit-enrollment/helper/diocese-approver-coverage-helper';
import { QueryArrayMessage } from '../../../../global/components/messages/query-array-message';

interface Props {
	mapPortalSettingsId?: string;
	dashboardMode?: boolean;
}

export function MapLanding(props: Props) {

	const epiApiState = useSelector<TcsmStates, TcsmEpiApiState>(state => state.epiProps);
	const siteSettings = useSelector<TcsmStates, TcsmSiteSettingsProps>(state => state.epiProps?.siteSettingsState?.siteSettings);
	const [dashboardUser, setDashboardUser] = React.useState(null);
	const dispatch = useDispatch();
	const language = useSelector<TcsmStates, string>(state => state.globalization.currentlanguage);
	const token = useDeserializedToken();

	useEffect(() => {
		dispatch(getNotifications(token?.person?.guid,
			(response) => dispatch(setNotifications(response?.data?.filter(a => a.notification_status === NotificationStatus.INITIAL || a.notification_status === NotificationStatus.DISMISSED))),
			() => dispatch(apiErrorClear()),
		));
	}, []);

	useEffect(() => {
		if (!props.dashboardMode) return;

		const guid = dashboardUser?.clientGuid;
		const roleMatrix = encodeURI(dashboardUser?.roleMatrix);

		if (props?.mapPortalSettingsId) {
			dispatch(getTcsmEpiProps(dispatch, props?.mapPortalSettingsId, { guid, roleMatrix }, language, false, setMapPortalSettings));
		}



	}, [dashboardUser]);

	const portalProps = useSelector<TcsmStates, MapPortalProps>(state => state.epiProps?.mapPortalSettingsState?.epiContent);
	const roles = useSelector<TcsmStates, string[]>(state => state?.authenticatedState?.roles);
	usePersonManagementReset();
	useGroupsReset();
	useSearchListsReset();
	useAdminGHLPendingEnrollments();
	removePendingEnrollmentLocalStorage();

	const newsUpdatesPath = useBasePath(`${ArcRoutes.ArcRoot}${ArcRoutes.NewsUpdates}`);
	// When a News & Updates page is configured on the MAP portal settings, the sidebar pulls its
	// announcements; otherwise it falls back to the TCSM news updates page items.
	const announcementsPageUrl = portalProps?.newsUpdatesPage;
	const newsUpdatesPageData = useEpiPageContent(announcementsPageUrl || newsUpdatesPath) as TcsmNewsUpdatesPage & RedesignNewsUpdatesPage;

	const notificationPath = useBasePath(`${ArcRoutes.ArcRoot}${ArcRoutes.Notifications}`);
	const adminPendingGhlEnrollments = useSelector<TcsmStates, AdminGhlPendingEnrollment[]>(state => state?.newHireEnrollmentState?.adminGhlpendingEnrollments);

	let name = props.dashboardMode ? dashboardUser?.name : `${token?.person?.fn} ${token?.person?.ln}`;

	if (!name) {
		name = '';
	}

	let matchedRoles = props.dashboardMode ? dashboardUser?.roleMatrix : portalProps?.roleNameMapping?.filter(a => roles?.includes(a.key))?.map(a => a?.value);

	if (!matchedRoles) {
		matchedRoles = [];
	}

	let roleMessage = matchedRoles?.length > 1 ?
		portalProps?.multipleAdminRoleText?.replace('{role}', matchedRoles?.join(', ')) :
		portalProps?.adminRoleText?.replace('{role}', matchedRoles[0]);

	if (matchedRoles?.length === 0) {
		roleMessage = '';
	}

	if (!epiApiState?.mapPortalSettingsState?.contentLoaded) {
		return <LoadingCircle />;
	}

	function UpdateDashboardData(du: DashboardUser) {
		setDashboardUser(du);
	}

	return (
		<>
			{!props.dashboardMode || <TcsmDashboardUsers setUserCallback={(u) => UpdateDashboardData(u)} />}
			<PageContainer>
				<PortalLandingBanner
					userFullName={name}
					userRole={roleMessage}
					userGreeting={siteSettings?.greetingText}
					brandImage={portalProps?.brandImage}
					brandImageAltText={portalProps?.brandImageAltText}
					bannerImageUrl={portalProps?.bannerImageLink}
					bannerImageAltText={portalProps?.bannerImageAltText}
					bannerImage={portalProps?.bannerImage} />
				<MainContent>
					<div className='main-content'>
						<QueryArrayMessage
							queryKey='success'
							useWarnings={[]}
						/>
						{
							adminPendingGhlEnrollments && adminPendingGhlEnrollments.length > 0 && (
								<NewHireAdminPendingGhlEnrollments settings={portalProps} />
							)
						}
						<DashboardNotifications notificationPath={notificationPath} />

						{
							portalProps?.myAdminPortalTitle &&
							<PageHeader headerText={portalProps?.myAdminPortalTitle} />
						}
						{
							portalProps?.pageIntro &&
							<DividerSection >
								<WysiwygWithReactLink content={portalProps?.pageIntro} />
							</DividerSection>
						}
						{props.dashboardMode || <PortalQuickActions />}
						<PortalGuidesAndResources guidesResourcesHeader={portalProps?.guidesResourcesHeader} guidesResourcesBlocks={portalProps?.guidesResourcesBlocks} />
						<WysiwygWithReactLink content={portalProps?.bottomMessage} />
					</div>
				</MainContent>

				<PortalSidebar
					latestUpdatesHeader={portalProps?.latestUpdatesHeader}
					sidebarInfoBoxHeader={portalProps?.sidebarInfoBoxHeader}
					sidebarInfoBoxContent={portalProps?.sidebarInfoBoxContent}
					newsUpdatesItems={newsUpdatesPageData?.newsUpdatesItems}
					newsUpdatesPath={newsUpdatesPath}
					viewMoreText={portalProps?.latestUpdatesViewMoreText}
					announcements={newsUpdatesPageData?.announcements}
					announcementsPageUrl={announcementsPageUrl}
				/>

			</PageContainer>
		</>
	);
}

MapLanding.defaultProps = {
	mapPortalSettingsId: '',
	dashboardMode: false,
};
